import { motion } from "framer-motion";
import { Search, Phone, TrendingDown, Globe } from "lucide-react";

const points = [
  {
    icon: Search,
    title: "97% of people search online for local businesses",
    desc: "If you don't show up with a real website, they call the competitor who does.",
  },
  {
    icon: Phone,
    title: "A Google listing alone isn't enough",
    desc: "Customers click through. They want to see your work, your team, and your prices before picking up the phone.",
  },
  {
    icon: TrendingDown,
    title: "Word-of-mouth has a ceiling",
    desc: "Referrals are great — but your business can only grow as fast as your last happy customer.",
  },
  {
    icon: Globe,
    title: "Competitors with websites look more professional",
    desc: "A clean website builds instant trust. Without one, you look smaller than you really are.",
  },
];

export const ProblemSection = () => {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative bg-gray-900 text-white py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-4">
            The reality in 2026
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight"
          >
            Your customers are{" "}
            <span className="text-blue-400">searching for you</span> online.
          </motion.h2>
          <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-2xl">
            Every day, someone in your service area types your trade and your
            town into Google. If they don't land on your website, they land on
            the competitor's.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 gap-5">
          {points.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors p-6"
                data-testid={`problem-card-${i}`}
              >
                <div className="h-10 w-10 rounded-md bg-blue-700/20 text-blue-400 flex items-center justify-center mb-4">
                  <Icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg md:text-xl text-white leading-snug">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                  {p.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
